import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ApiError, api } from "@/lib/api";
import { useApiMutation } from "@/lib/use-api-mutation";
import { Link, createFileRoute, redirect, useNavigate, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";

/**
 * `/accounting/contacts` — the address book.
 *
 * The list is a list: a row leads to the contact's own page rather than
 * unfolding a panel underneath, so a contact has an address that can be
 * linked to from a document, a customer, or a message.
 *
 * Everybody we invoice or are invoiced by, in one list. A partner that
 * is both sides at once is one row, not two — which is the whole reason
 * this page exists next to Customers: a customer is a project
 * relationship, a contact is a billing identity, and the landlord is
 * only ever the second.
 */

const searchSchema = z.object({
  role: z.enum(["all", "customer", "supplier", "both"]).optional(),
  q: z.string().optional(),
});

type Contact = {
  id: string;
  name: string;
  role: "customer" | "supplier" | "both";
  registrationNo: string | null;
  vatNo: string | null;
  email: string | null;
  phone: string | null;
  street: string | null;
  city: string | null;
  zip: string | null;
  country: string;
  defaultDueDays: number | null;
  currency: string;
  note: string | null;
  active: boolean;
  customerCount: number;
  issuedCount: number;
  receivedCount: number;
};

const ROLE_LABEL: Record<Contact["role"], string> = {
  customer: "customer",
  supplier: "supplier",
  both: "customer + supplier",
};

export const Route = createFileRoute("/_app/accounting/contacts/")({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => ({ role: search.role, q: search.q }),
  loader: async ({ deps }) => {
    const params = new URLSearchParams();
    if (deps.role && deps.role !== "all") params.set("role", deps.role);
    if (deps.q) params.set("q", deps.q);
    const qs = params.toString();

    try {
      return await api.get<{ contacts: Contact[] }>(
        `/api/accounting/contacts${qs ? `?${qs}` : ""}`,
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) throw redirect({ to: "/my" });
      throw err;
    }
  },
  component: ContactsPage,
});

function ContactsPage() {
  const { contacts } = Route.useLoaderData();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const router = useRouter();
  const [adding, setAdding] = useState(false);

  const role = search.role ?? "all";

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-semibold text-2xl">Contacts</h1>
          <p className="text-muted-foreground text-sm">
            Everybody we invoice or are invoiced by. A Hub customer bills through the contact it is
            linked to — the contact carries the identity that lands on the document.
          </p>
        </div>
        <Button onClick={() => setAdding((value) => !value)}>
          {adding ? "Close" : "New contact"}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {(["all", "customer", "supplier", "both"] as const).map((option) => (
          <button
            key={option}
            type="button"
            className={`rounded border px-3 py-1 text-sm ${role === option ? "bg-muted" : ""}`}
            onClick={() =>
              navigate({ to: "/accounting/contacts", search: { ...search, role: option } })
            }
          >
            {option === "all" ? "All" : ROLE_LABEL[option]}
          </button>
        ))}
        <input
          defaultValue={search.q ?? ""}
          placeholder="Name, registration or VAT number…"
          className="h-9 min-w-64 flex-1 rounded-md border border-input bg-background px-3 text-sm"
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            navigate({
              to: "/accounting/contacts",
              search: { ...search, q: e.currentTarget.value || undefined },
            });
          }}
        />
      </div>

      {adding && (
        <ContactForm
          onDone={() => {
            setAdding(false);
            void router.invalidate();
          }}
        />
      )}

      <Card>
        <CardContent className="pt-6">
          {contacts.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              Nothing here yet — the Fakturoid import fills the address book, or add a contact by
              hand.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-1 pr-4 font-medium">Name</th>
                  <th className="py-1 pr-4 font-medium">Role</th>
                  <th className="py-1 pr-4 font-medium">Registration</th>
                  <th className="py-1 pr-4 font-medium">VAT no.</th>
                  <th className="py-1 pr-4 font-medium">Linked customers</th>
                  <th className="py-1 pr-4 text-right font-medium">Documents</th>
                  <th className="py-1 font-medium" />
                </tr>
              </thead>
              <tbody>
                {contacts.map((contact) => (
                  <tr key={contact.id} className="border-t">
                    <td className="py-1 pr-4">
                      <Link
                        to="/accounting/contacts/$id"
                        params={{ id: contact.id }}
                        className="font-medium hover:underline"
                      >
                        {contact.name}
                      </Link>
                    </td>
                    <td className="py-1 pr-4">
                      <Badge variant={contact.role === "both" ? "success" : "outline"}>
                        {ROLE_LABEL[contact.role]}
                      </Badge>
                    </td>
                    <td className="py-1 pr-4 tabular-nums">{contact.registrationNo ?? "—"}</td>
                    <td className="py-1 pr-4">{contact.vatNo ?? "—"}</td>
                    <td className="py-1 pr-4 text-muted-foreground">
                      {contact.customerCount > 0 ? contact.customerCount : "—"}
                    </td>
                    <td className="py-1 pr-4 text-right tabular-nums">
                      {contact.issuedCount + contact.receivedCount}
                    </td>
                    <td className="py-1">
                      <Link
                        to="/accounting/contacts/$id"
                        params={{ id: contact.id }}
                        className="text-sm underline"
                      >
                        Profile
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ContactForm({ onDone }: { onDone: () => void }) {
  const create = useApiMutation(
    (body: Record<string, unknown>) => api.post("/api/accounting/contacts", body),
    { onSuccess: onDone, errorMessage: () => "Couldn't create the contact." },
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">New contact</CardTitle>
        <CardDescription>
          A registration number is checked against the book — one company, one row.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="grid gap-3 md:grid-cols-3"
          onSubmit={(e) => {
            e.preventDefault();
            const form = new FormData(e.currentTarget);
            const value = (key: string) => {
              const raw = String(form.get(key) ?? "").trim();
              return raw.length > 0 ? raw : null;
            };
            create.mutate({
              name: String(form.get("name") ?? "").trim(),
              role: String(form.get("role") ?? "customer"),
              registrationNo: value("registrationNo"),
              vatNo: value("vatNo"),
              email: value("email"),
              street: value("street"),
              city: value("city"),
              zip: value("zip"),
              country: String(form.get("country") ?? "CZ"),
              defaultDueDays: dueDays(form),
            });
          }}
        >
          <Field name="name" label="Name" required />
          <label className="text-sm">
            <span className="text-muted-foreground text-xs">Role</span>
            <select
              name="role"
              className="mt-1 block h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="customer">customer</option>
              <option value="supplier">supplier</option>
              <option value="both">customer + supplier</option>
            </select>
          </label>
          <Field name="registrationNo" label="Registration no." />
          <Field name="vatNo" label="VAT no." />
          <Field name="email" label="Email" type="email" />
          <Field name="street" label="Street" />
          <Field name="zip" label="ZIP" />
          <Field name="city" label="City" />
          <Field name="country" label="Country" defaultValue="CZ" />
          <Field
            name="defaultDueDays"
            label="Due in (days)"
            type="number"
            placeholder="Default from settings"
          />
          <div className="flex items-end">
            <Button type="submit" disabled={create.pending}>
              {create.pending ? "Creating…" : "Create"}
            </Button>
          </div>
          {create.error && <p className="text-destructive text-sm md:col-span-3">{create.error}</p>}
        </form>
      </CardContent>
    </Card>
  );
}

const dueDays = (form: FormData): number | null => {
  const raw = String(form.get("defaultDueDays") ?? "").trim();
  return raw.length > 0 ? Number(raw) : null;
};

function Field(props: {
  name: string;
  label: string;
  type?: string;
  defaultValue?: string;
  placeholder?: string;
  required?: boolean;
}) {
  const { label, ...rest } = props;
  return (
    <label className="text-sm">
      <span className="text-muted-foreground text-xs">{label}</span>
      <input
        {...rest}
        className="mt-1 block h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
      />
    </label>
  );
}
